import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { branchesQuery, menuItemQuery, publicBranchMenuQuery } from "@/lib/queries";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useStorefront, useStorefrontConfig } from "@/lib/storefront";
import { loc } from "@/lib/format";
import { hrefWithLanguage } from "@/lib/seo-language";
import { branchPage, productSlugFromPath, seoPage } from "@/lib/seo-page";
import type { Branch, Lang, ResponsiveImageSource } from "@/lib/types";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function SeoController({ pathname }: { pathname: string }) {
  const router = useRouter();
  const { lang } = useI18n();
  const storefront = useStorefront();
  const config = useStorefrontConfig();
  const { cart } = useCart();
  const branchMatch = pathname.match(branchPage);
  const branchId = branchMatch?.[1] ?? null;
  const isBranchMenu = Boolean(branchMatch?.[2]);
  const productSlug = productSlugFromPath(pathname);
  const branches = useQuery({ ...branchesQuery(), enabled: Boolean(branchMatch) || pathname === "/branches" || pathname.startsWith("/branchMenu/") });
  const branch = branches.data?.find((item) => item.id === branchId);
  const product = useQuery(menuItemQuery(productSlug, cart.orderType?.branchId ?? "", cart.orderType?.type));
  const branchMenu = useQuery(publicBranchMenuQuery(
    isBranchMenu && branch ? branch.id : "",
    Boolean(storefront.settings?.onlineOrderingEnabled),
    Boolean(storefront.settings?.dineInEnabled && branch?.indoorService),
  ));

  useEffect(() => {
    const page = seoPage({
      pathname, lang, branch, product: product.data ?? undefined, branches: branches.data, storefront, config,
      branchMenuHasItems: Boolean(branchMenu.data?.data.items.some((item) => item.item_type === "simple" || item.variants.some((variant) => variant.available))),
    });
    const supportedLocales = (["ar", "en"] as const).filter((locale) =>
      storefront.tenant.enabledLocales.includes(locale),
    );
    const pageLocales = supportedLocales.filter((locale) => {
      if (pathname === "/branches") return Boolean(branches.data?.every((item) => item.translatedLocales?.includes(locale) ?? true));
      if (productSlug) return Boolean(product.data?.name[locale]?.trim());
      if (!branchId) return true;
      if (!branch?.translatedLocales?.includes(locale)) return false;
      if (!isBranchMenu) return true;
      return Boolean(branchMenu.data?.data.items.every((item) => item.name_i18n[locale]?.trim()) &&
        branchMenu.data?.data.categories.every((category) => category.name_i18n[locale]?.trim()));
    });
    const index = page.index && pageLocales.includes(lang);
    const origin = `https://${storefront.tenant.canonicalHost ?? window.location.hostname}`;
    const localizedHref = hrefWithLanguage(router.state.location.href, lang);
    if (page.index && storefront.tenant.enabledLocales.includes(lang) && localizedHref) {
      void router.navigate({ href: localizedHref, replace: true, resetScroll: false });
    }
    document.title = page.title;
    setMeta("name", "description", page.description);
    setMeta("name", "robots", index ? "index,follow" : "noindex,follow");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", loc(config.title, lang));
    setMeta("property", "og:title", page.title);
    setMeta("property", "og:description", page.description);
    if (/^[A-Z]{2}$/.test(storefront.tenant.countryCode)) {
      setMeta("property", "og:locale", `${lang}_${storefront.tenant.countryCode}`);
    }
    setMeta("name", "twitter:title", page.title);
    setMeta("name", "twitter:description", page.description);

    if (index) {
      const canonical = `${origin}${pathname}?lang=${lang}`;
      setLink("canonical", canonical);
      setMeta("property", "og:url", canonical);
      for (const locale of ["en", "ar"] as const) {
        const alternate = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${locale}"]`);
        if (pageLocales.includes(locale)) {
          const link = alternate ?? document.head.appendChild(document.createElement("link"));
          link.rel = "alternate";
          link.hreflang = locale;
          link.href = `${origin}${pathname}?lang=${locale}`;
        } else alternate?.remove();
      }
      const defaultAlternate = document.querySelector<HTMLLinkElement>('link[rel="alternate"][hreflang="x-default"]');
      if (pageLocales.includes(storefront.tenant.defaultLocale)) {
        const link = defaultAlternate ?? document.head.appendChild(document.createElement("link"));
        link.rel = "alternate";
        link.hreflang = "x-default";
        link.href = `${origin}${pathname}?lang=${storefront.tenant.defaultLocale}`;
      } else defaultAlternate?.remove();
    } else {
      document.querySelector('link[rel="canonical"]')?.remove();
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
      document.querySelector('meta[property="og:url"]')?.remove();
    }

    const image = absoluteImage(page.image, origin);
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("property", "og:image:alt", "imageAlt" in page ? page.imageAlt : loc(config.title, lang));
      setMeta("name", "twitter:image", image);
    } else {
      document.querySelector('meta[property="og:image"]')?.remove();
      document.querySelector('meta[property="og:image:alt"]')?.remove();
      document.querySelector('meta[name="twitter:image"]')?.remove();
    }

    document.getElementById("storefront-restaurant-data")?.remove();
    if (page.restaurant && index) {
      const script = document.createElement("script");
      script.id = "storefront-restaurant-data";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(restaurantData(page.restaurant, lang, storefront.tenant.countryCode, `${origin}${pathname}?lang=${lang}`, page.restaurant.hasPhoto ? image : null));
      document.head.appendChild(script);
    }
  }, [branch, branchId, branches.data, branchMenu.data, config, isBranchMenu, lang, pathname, product.data, productSlug, router, storefront]);

  return null;
}

function restaurantData(branch: Branch, lang: Lang, countryCode: string, url: string, image: string | null) {
  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: loc(branch.name, lang),
    url,
    ...(loc(branch.address, lang) ? { address: { "@type": "PostalAddress", streetAddress: loc(branch.address, lang), addressCountry: countryCode } } : {}),
    ...(branch.phones[0] ? { telephone: branch.phones[0] } : {}),
    ...(image ? { image } : {}),
    ...(branch.coordinates ? { geo: { "@type": "GeoCoordinates", latitude: branch.coordinates.lat, longitude: branch.coordinates.lng } } : {}),
    openingHoursSpecification: (branch.weeklyHours ?? []).filter((hours) => !hours.closed && hours.opensAt && hours.closesAt).map((hours) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${days[hours.dayOfWeek]}`,
      opens: hours.opensAt,
      closes: hours.closesAt,
    })),
  };
}

function sourceUrl(source: ResponsiveImageSource | undefined): string | null {
  if (!source) return null;
  return typeof source === "string" ? source : source.src;
}

function absoluteImage(source: ResponsiveImageSource | undefined, origin: string): string | null {
  const src = sourceUrl(source);
  if (!src) return null;
  try {
    const url = new URL(src, origin);
    return url.protocol === "https:" ? url.href : null;
  } catch { return null; }
}

function setMeta(kind: "name" | "property", key: string, content: string) {
  const meta = document.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`) ?? document.head.appendChild(document.createElement("meta"));
  meta.setAttribute(kind, key);
  meta.content = content;
}

function setLink(rel: string, href: string) {
  const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`) ?? document.head.appendChild(document.createElement("link"));
  link.rel = rel;
  link.href = href;
}
